import { useMemo, useState } from "react";
import React from "react";
import { I_Msg, I_Phone, I_Plus, I_Search, I_Users } from "./icons";
import { Ava, Pill } from "./ui";
import { BUDGETS, PRIORITIES, STATUSES_C, pStyle, sStyle } from "../constants.js";
import { fmtDate, normBucket, telHref, waHref } from "../lib/format.js";
import { btnP, glass, glassHov, heading, input, monoFont } from "../styles.js";

// Sub-sections of the client list. Allotment is the `allotted` boolean, never `status`.
const ALLOTMENT_TABS = [
  { id: "all", label: "All clients", title: "Clients", test: () => true },
  { id: "allotted", label: "Allotted", title: "Allotted clients", test: (c) => !!c.allotted },
  { id: "unallotted", label: "Unallotted", title: "Unallotted clients", test: (c) => !c.allotted },
];

// Clients without a request date always sort last.
const byRequestDate = (dir) => (a, b) => {
  if (!a.request_date) return b.request_date ? 1 : 0;
  if (!b.request_date) return -1;
  return dir * a.request_date.localeCompare(b.request_date);
};

const ClientsView = ({ clients, onOpen, onNew, allotment = "all", onAllotment }) => {
    const tab = ALLOTMENT_TABS.find((t) => t.id === allotment) || ALLOTMENT_TABS[0];
    const [q, setQ] = useState(""),
      [pri, setPri] = useState(""),
      [bud, setBud] = useState(""),
      // the sub-sections already split by allotment, so start them on every status
      [st, setSt] = useState(allotment === "all" ? "Active" : ""),
      [sanc, setSanc] = useState(""),
      [sort, setSort] = useState(allotment === "unallotted" ? "req-asc" : "updated");
    const inTab = useMemo(() => clients.filter(tab.test), [clients, tab]);
    const filtered = useMemo(() => {
      let l = inTab;
      if (q) {
        const x = q.toLowerCase();
        l = l.filter((c) =>
          [c.name, c.phone, c.requirement_type, c.locality_pref, c.notes, c.code].some((f) =>
            (f || "").toLowerCase().includes(x),
          ),
        );
      }
      if (pri) l = l.filter((c) => c.priority === pri);
      if (bud) {
        const _t = normBucket(bud);
        l = l.filter((c) => normBucket(c.budget_bucket) === _t);
      }
      if (st === "Allotted") l = l.filter((c) => !!c.allotted);
      else if (st) l = l.filter((c) => c.status === st);
      if (sanc === "yes") l = l.filter((c) => !!c.sanctioned);
      else if (sanc === "no") l = l.filter((c) => !c.sanctioned);
      if (sort === "req-asc") l = [...l].sort(byRequestDate(1));
      else if (sort === "req-desc") l = [...l].sort(byRequestDate(-1));
      return l;
    }, [inTab, q, pri, bud, st, sanc, sort]);
    const FilterChip = ({ on, onClick, children, dot }) => (
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition ${on ? "bg-amber-200 text-slate-900" : "bg-white/[0.04] border border-white/[0.08] text-slate-300 hover:bg-white/[0.08]"}`}
      >
        {dot && !on && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
        {children}
      </button>
    );
    return (
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl text-slate-100 mb-1" style={heading}>
              {tab.title}
            </h1>
            <p className="text-sm text-slate-400">
              {filtered.length} of {inTab.length} shown
            </p>
          </div>
          <button onClick={onNew} className={btnP}>
            <I_Plus size={14} /> New client
          </button>
        </div>
        {onAllotment && (
          <div className="flex gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/[0.08] w-full sm:w-fit">
            {ALLOTMENT_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => onAllotment(t.id)}
                className={`flex-1 sm:flex-none px-3.5 py-1.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 ${allotment === t.id ? "bg-amber-200 text-slate-900" : "text-slate-400 hover:text-slate-100 hover:bg-white/[0.06]"}`}
              >
                {t.label}
                <span
                  className={`text-[11px] ${allotment === t.id ? "text-slate-700" : "text-slate-500"}`}
                  style={monoFont}
                >
                  {clients.filter(t.test).length}
                </span>
              </button>
            ))}
          </div>
        )}
        <div className={`${glass} rounded-xl p-3 space-y-3`}>
          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            <div className="relative flex-1 min-w-[200px]">
              <I_Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, phone, requirement, locality…"
                className={`${input} pl-9`}
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className={`${input} sm:w-auto`}
              aria-label="Sort clients"
            >
              <option value="updated">Recently updated</option>
              <option value="req-asc">Request date · oldest first</option>
              <option value="req-desc">Request date · newest first</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Status</span>
            <FilterChip on={!st} onClick={() => setSt("")}>
              All
            </FilterChip>
            {STATUSES_C.filter((s) => allotment === "all" || s !== "Allotted").map((s) => (
              <FilterChip
                key={s}
                on={st === s}
                onClick={() => setSt(st === s ? "" : s)}
                dot={sStyle[s]?.dot}
              >
                {s}
              </FilterChip>
            ))}
            <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-3 mr-1">
              Priority
            </span>
            <FilterChip on={!pri} onClick={() => setPri("")}>
              Any
            </FilterChip>
            {PRIORITIES.map((p) => (
              <FilterChip
                key={p}
                on={pri === p}
                onClick={() => setPri(pri === p ? "" : p)}
                dot={pStyle[p].dot}
              >
                {p}
              </FilterChip>
            ))}
            <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-3 mr-1">
              Sanctioned
            </span>
            <FilterChip on={!sanc} onClick={() => setSanc("")}>
              Any
            </FilterChip>
            <FilterChip
              on={sanc === "yes"}
              onClick={() => setSanc(sanc === "yes" ? "" : "yes")}
              dot="bg-emerald-400"
            >
              Yes
            </FilterChip>
            <FilterChip
              on={sanc === "no"}
              onClick={() => setSanc(sanc === "no" ? "" : "no")}
              dot="bg-stone-400"
            >
              No
            </FilterChip>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-3 mr-1">
              Budget
            </span>
            <FilterChip on={!bud} onClick={() => setBud("")}>
              Any
            </FilterChip>
            {BUDGETS.slice(0, 7).map((b) => (
              <FilterChip key={b} on={bud === b} onClick={() => setBud(bud === b ? "" : b)}>
                {b}
              </FilterChip>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className={`${glass} rounded-2xl p-12 text-center`}>
            <I_Users size={32} className="mx-auto mb-2 text-slate-600" />
            <div className="text-sm text-slate-400 mb-3">
              {clients.length === 0
                ? "No clients yet."
                : inTab.length === 0
                  ? `No ${tab.label.toLowerCase()} clients.`
                  : "Nothing matches your filters."}
            </div>
            {clients.length === 0 && (
              <button onClick={onNew} className={btnP}>
                <I_Plus size={14} />
                Add your first client
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => onOpen(c.id)}
                className={`${glass} ${glassHov} rounded-xl p-4 text-left transition cv-auto`}
              >
                <div className="flex items-start gap-3.5">
                  <Ava name={c.name} photo={c.photo} size="xl" />
                  <div className="min-w-0 flex-1">
                    <div className="text-base text-slate-100 truncate" style={heading}>
                      {c.name}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                      {c.requirement_type && <span>{c.requirement_type}</span>}
                      {c.budget_bucket && (
                        <React.Fragment>
                          <span className="text-slate-600">·</span>
                          <span style={monoFont}>{c.budget_bucket}</span>
                        </React.Fragment>
                      )}
                    </div>
                    {c.request_date && (
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Requested {fmtDate(c.request_date)}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {c.status && (
                        <Pill label={c.status} s={sStyle[c.status] || sStyle.Active} small={true} />
                      )}
                      {c.allotted && <Pill label="Allotted" s={sStyle.Allotted} small={true} />}
                      {c.sanctioned && (
                        <Pill
                          label="Sanctioned"
                          s={{
                            dot: "bg-emerald-400",
                            text: "text-emerald-200",
                            bg: "bg-emerald-400/10",
                            ring: "ring-emerald-400/20",
                          }}
                          small={true}
                        />
                      )}
                      {c.priority && c.priority !== "Medium" && (
                        <Pill
                          label={c.priority}
                          s={{
                            ...pStyle[c.priority],
                            bg: "bg-white/[0.04]",
                            ring: "ring-white/[0.08]",
                          }}
                          small={true}
                        />
                      )}
                    </div>
                  </div>
                </div>
                {c.phone && (
                  <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-white/[0.06]">
                    <a
                      onClick={(e) => e.stopPropagation()}
                      href={telHref(c.phone)}
                      className="flex-1 text-center text-[11px] py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 transition flex items-center justify-center gap-1"
                    >
                      <I_Phone size={11} />
                      Call
                    </a>
                    <a
                      onClick={(e) => e.stopPropagation()}
                      href={waHref(c.whatsapp || c.phone, "")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center text-[11px] py-1.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 transition flex items-center justify-center gap-1"
                    >
                      <I_Msg size={11} />
                      WhatsApp
                    </a>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

export { ClientsView };
